/* oxlint-disable jsx-a11y/no-noninteractive-element-to-interactive-role, jsx-a11y/prefer-tag-over-role, jsx-a11y/no-redundant-roles -- WAI-ARIA APG 날짜 선택 대화상자(grid)와 시각 listbox 패턴 */
import { Popover as BasePopover } from '@base-ui/react/popover';
import {
  addDays,
  addMonths,
  clampDateKey,
  endOfWeekKey,
  formatDateKeyLong,
  formatDateKeyShort,
  formatTimeOfDay,
  formatYearMonth,
  isDateKey,
  isTimeOfDay,
  monthGridKeys,
  parseDateKey,
  startOfWeekKey,
  timeOfDayOptions,
  todayDateKey,
  WEEKDAY_HEADERS_MONDAY_FIRST,
} from '@labflow/shared';
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import {
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';

import { cn } from '../lib/cn';
import { Button } from './button';
import { Icon, type IconProps } from './icon';
import { inputClassName } from './input';
import { PopoverPopup } from './popover';

const WEEKDAY_NAMES = ['월요일', '화요일', '수요일', '목요일', '금요일', '토요일', '일요일'];

/** 트리거와 폼 배선 — `Field`가 넘기는 `id`·`aria-describedby`·`aria-invalid`·`required`를 받는다. */
interface PickerBaseProps {
  /** 비었으면 빈 문자열 */
  value: string;
  onValueChange: (value: string) => void;
  /** `YYYY-MM-DD`, 이 날을 포함한다 */
  min?: string;
  /** `YYYY-MM-DD`, 이 날을 포함한다 */
  max?: string;
  isDateDisabled?: (date: string) => boolean;
  /** 켜면 `비우기`가 없다 */
  required?: boolean;
  disabled?: boolean;
  /** 폼 제출에 실을 이름 — 값 그대로 보낸다 */
  name?: string;
  placeholder?: string;
  /** 오늘 표시와 `오늘`의 기준. 기본은 지금의 `timeZone` 날짜 */
  today?: string;
  timeZone?: string;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

export type DatePickerProps = PickerBaseProps;

export interface DateTimePickerProps extends PickerBaseProps {
  /** `YYYY-MM-DDTHH:mm`(그 시간대의 벽시계 시각) */
  value: string;
  /** 시각 목록 간격(분). 목록에 없는 기존 값은 제자리에 끼운다 */
  minuteStep?: number;
}

function useToday(today: string | undefined, timeZone: string) {
  const [computed] = useState(() => todayDateKey(new Date(), timeZone));
  return today ?? computed;
}

function isSelectable(
  key: string,
  { min, max, isDateDisabled }: Pick<PickerBaseProps, 'min' | 'max' | 'isDateDisabled'>,
) {
  if (min && key < min) return false;
  if (max && key > max) return false;
  return !isDateDisabled?.(key);
}

/* ---------- 트리거 ---------- */

interface TriggerProps extends Pick<
  PickerBaseProps,
  | 'disabled'
  | 'id'
  | 'className'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-describedby'
  | 'aria-invalid'
> {
  text: string | null;
  placeholder: string;
  icon: IconProps['as'];
  ref?: Ref<HTMLButtonElement>;
}

function PickerTrigger({
  text,
  placeholder,
  icon,
  ref,
  className,
  'aria-describedby': describedBy,
  ...props
}: TriggerProps) {
  const valueId = useId();
  return (
    <BasePopover.Trigger
      ref={ref}
      type="button"
      aria-describedby={[valueId, describedBy].filter(Boolean).join(' ')}
      className={cn(
        inputClassName,
        'gap-snug flex h-10 items-center justify-between text-left',
        'data-[popup-open]:border-line-accent',
        className,
      )}
      {...props}
    >
      <span id={valueId} className={cn('truncate', text ? 'text-ink' : 'text-ink-faint')}>
        {text ?? placeholder}
      </span>
      <Icon as={icon} size="sm" className="text-ink-faint shrink-0" />
    </BasePopover.Trigger>
  );
}

/** 폼 제출용 값. `required`면 브라우저 검증이 비었을 때 막고, 초점은 트리거로 돌린다. */
function FormValue({
  name,
  value,
  required,
  onFocusRedirect,
}: {
  name?: string;
  value: string;
  required?: boolean;
  onFocusRedirect: () => void;
}) {
  if (!name && !required) return null;
  return (
    <input
      aria-hidden="true"
      tabIndex={-1}
      className="sr-only"
      name={name}
      value={value}
      required={required}
      onChange={() => {}}
      onFocus={onFocusRedirect}
    />
  );
}

/* ---------- 달력 ---------- */

interface CalendarProps extends Pick<PickerBaseProps, 'min' | 'max' | 'isDateDisabled'> {
  selected: string | null;
  today: string;
  onSelect: (date: string) => void;
  gridRef: Ref<HTMLTableElement>;
  /** 열릴 때 처음 볼 날짜 */
  initialFocus: string;
  footer?: ReactNode;
  /** 바깥(`오늘` 등)에서 보는 달과 초점 날짜를 옮긴다 */
  controlRef?: Ref<CalendarControl>;
}

interface CalendarControl {
  show: (date: string) => void;
}

function Calendar({
  selected,
  today,
  onSelect,
  gridRef,
  initialFocus,
  min,
  max,
  isDateDisabled,
  footer,
  controlRef,
}: CalendarProps) {
  const headingId = useId();
  const [focusKey, setFocusKey] = useState(initialFocus);
  const [view, setView] = useState(() => {
    const { year, month } = parseDateKey(initialFocus);
    return { year, month };
  });
  const tableRef = useRef<HTMLTableElement | null>(null);
  const moveFocus = useRef(false);

  useLayoutEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    tableRef.current?.querySelector<HTMLElement>(`[data-date="${focusKey}"]`)?.focus();
  }, [focusKey]);

  const show = (key: string) => {
    const { year, month } = parseDateKey(key);
    setView({ year, month });
  };

  const goTo = (key: string, focus: boolean) => {
    const next = clampDateKey(key, min, max);
    moveFocus.current = focus;
    setFocusKey(next);
    show(next);
  };

  useImperativeHandle(controlRef, () => ({ show: (date: string) => goTo(date, false) }));

  const shiftView = (delta: number) => {
    const firstOfView = `${view.year}-${String(view.month).padStart(2, '0')}-01`;
    const target = addMonths(firstOfView, delta);
    const { year, month } = parseDateKey(target);
    setView({ year, month });
    setFocusKey(
      clampDateKey(
        addMonths(focusKey, delta).slice(0, 7) === target.slice(0, 7)
          ? addMonths(focusKey, delta)
          : target,
        min,
        max,
      ),
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const moves: Record<string, () => string> = {
      ArrowLeft: () => addDays(focusKey, -1),
      ArrowRight: () => addDays(focusKey, 1),
      ArrowUp: () => addDays(focusKey, -7),
      ArrowDown: () => addDays(focusKey, 7),
      Home: () => startOfWeekKey(focusKey),
      End: () => endOfWeekKey(focusKey),
      PageUp: () => addMonths(focusKey, event.shiftKey ? -12 : -1),
      PageDown: () => addMonths(focusKey, event.shiftKey ? 12 : 1),
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    goTo(move(), true);
  };

  const viewFirst = `${view.year}-${String(view.month).padStart(2, '0')}-01`;
  const prevDisabled = Boolean(min && addDays(viewFirst, -1) < min);
  const nextDisabled = Boolean(max && addMonths(viewFirst, 1) > max);
  const cells = monthGridKeys(view.year, view.month);
  const weeks = Array.from({ length: 6 }, (_, w) => cells.slice(w * 7, w * 7 + 7));

  return (
    <div className="gap-snug flex flex-col">
      <div className="flex items-center justify-between">
        <h2 id={headingId} aria-live="polite" className="text-fg ps-tight text-sm font-semibold">
          {formatYearMonth(view.year, view.month)}
        </h2>
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="이전 달"
            disabled={prevDisabled}
            onClick={() => shiftView(-1)}
          >
            <Icon as={ChevronLeft} size="sm" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="다음 달"
            disabled={nextDisabled}
            onClick={() => shiftView(1)}
          >
            <Icon as={ChevronRight} size="sm" />
          </Button>
        </div>
      </div>
      <table
        ref={(node) => {
          tableRef.current = node;
          if (typeof gridRef === 'function') gridRef(node);
          else if (gridRef) gridRef.current = node;
        }}
        role="grid"
        aria-labelledby={headingId}
        className="border-collapse"
      >
        <thead>
          <tr>
            {WEEKDAY_HEADERS_MONDAY_FIRST.map((label, i) => (
              <th
                key={label}
                scope="col"
                abbr={WEEKDAY_NAMES[i]}
                className="text-ink-muted h-8 w-9 text-xs font-medium"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]!.key}>
              {week.map((cell) => {
                const isSelected = cell.key === selected;
                const isToday = cell.key === today;
                const selectable = isSelectable(cell.key, { min, max, isDateDisabled });
                return (
                  <td
                    key={cell.key}
                    role="gridcell"
                    data-date={cell.key}
                    tabIndex={cell.key === focusKey ? 0 : -1}
                    aria-selected={isSelected}
                    aria-label={`${formatDateKeyLong(cell.key)}${isToday ? ', 오늘' : ''}`}
                    aria-current={isToday ? 'date' : undefined}
                    aria-disabled={selectable ? undefined : true}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        if (selectable) onSelect(cell.key);
                        return;
                      }
                      onKeyDown(event);
                    }}
                    onFocus={() => {
                      if (cell.key !== focusKey) setFocusKey(cell.key);
                    }}
                    onClick={() => {
                      if (selectable) onSelect(cell.key);
                    }}
                    className={cn(
                      'relative size-9 cursor-default rounded-lg p-0 text-center text-sm tabular-nums',
                      'ease-flow duration-fast transition-colors',
                      isSelected
                        ? 'bg-action text-action-fg hover:bg-action-hover font-semibold'
                        : !selectable
                          ? 'text-disabled-fg cursor-not-allowed line-through'
                          : cn(
                              'hover:bg-highlight',
                              cell.inMonth ? 'text-ink' : 'text-ink-faint',
                              isToday && 'text-fg font-semibold',
                            ),
                    )}
                  >
                    {cell.day}
                    {isToday ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full',
                          isSelected ? 'bg-action-fg' : 'bg-fg',
                        )}
                      />
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {footer ? (
        <div className="border-line pt-snug flex items-center justify-between border-t">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

function firstGridFocus(gridRef: { current: HTMLTableElement | null }) {
  return () => gridRef.current?.querySelector<HTMLElement>('[data-date][tabindex="0"]') ?? null;
}

function pickerInitialFocus(
  value: string | null,
  today: string,
  range: Pick<PickerBaseProps, 'min' | 'max'>,
) {
  return clampDateKey(value ?? today, range.min, range.max);
}

/* ---------- DatePicker ---------- */

/**
 * 날짜 하나를 고른다. 값은 시간대 없는 `YYYY-MM-DD`이고 오늘 표시만 `timeZone`(기본 서울)을 따른다.
 * 팝오버는 WAI-ARIA 날짜 선택 대화상자 패턴이다 — 방향키·PageUp/Down·Home/End로 옮기고 Enter로 고른다.
 */
export function DatePicker({
  value,
  onValueChange,
  min,
  max,
  isDateDisabled,
  required,
  disabled,
  name,
  placeholder = '날짜 선택',
  today: todayProp,
  timeZone = 'Asia/Seoul',
  ...triggerProps
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const today = useToday(todayProp, timeZone);
  const gridRef = useRef<HTMLTableElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selected = isDateKey(value) ? value : null;
  const range = { min, max, isDateDisabled };

  const choose = (next: string) => {
    onValueChange(next);
    setOpen(false);
  };

  return (
    <BasePopover.Root open={open} onOpenChange={setOpen} modal="trap-focus">
      <PickerTrigger
        ref={triggerRef}
        text={selected ? formatDateKeyShort(selected) : null}
        placeholder={placeholder}
        icon={CalendarDays}
        disabled={disabled}
        {...triggerProps}
      />
      <FormValue
        name={name}
        value={selected ?? ''}
        required={required}
        onFocusRedirect={() => triggerRef.current?.focus()}
      />
      <BasePopover.Portal>
        <BasePopover.Positioner sideOffset={4} align="start" className="z-popover">
          <PopoverPopup
            aria-label="날짜 선택"
            initialFocus={firstGridFocus(gridRef)}
            className="p-row w-auto"
          >
            <Calendar
              selected={selected}
              today={today}
              gridRef={gridRef}
              initialFocus={pickerInitialFocus(selected, today, range)}
              onSelect={choose}
              {...range}
              footer={
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!isSelectable(today, range)}
                    onClick={() => choose(today)}
                  >
                    오늘
                  </Button>
                  {!required && selected ? (
                    <Button variant="ghost" size="sm" onClick={() => choose('')}>
                      비우기
                    </Button>
                  ) : null}
                </>
              }
            />
          </PopoverPopup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}

/* ---------- DateTimePicker ---------- */

function splitDateTime(value: string): { date: string | null; time: string | null } {
  const [date = '', time = ''] = value.split('T');
  return { date: isDateKey(date) ? date : null, time: isTimeOfDay(time) ? time : null };
}

/**
 * 날짜와 시각을 함께 고른다. 값은 `YYYY-MM-DDTHH:mm`(그 시간대의 벽시계 시각)이다.
 * 날짜를 고르면 팝오버가 남고, 시각을 고르면 값을 내고 닫힌다. 날짜만 바꾸고 바깥을 누르면
 * 그 날짜로 내고(값이 비어 있으면 시각을 골라야 낸다), Esc는 바꾼 것을 버린다.
 */
export function DateTimePicker({
  value,
  onValueChange,
  min,
  max,
  isDateDisabled,
  required,
  disabled,
  name,
  placeholder = '날짜와 시각 선택',
  today: todayProp,
  timeZone = 'Asia/Seoul',
  minuteStep = 30,
  ...triggerProps
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const today = useToday(todayProp, timeZone);
  const gridRef = useRef<HTMLTableElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const current = splitDateTime(value);
  const committed = current.date && current.time ? current : null;
  const [draftDate, setDraftDate] = useState<string | null>(null);
  const calendarRef = useRef<CalendarControl>(null);
  const range = { min, max, isDateDisabled };

  const commit = (date: string, time: string) => {
    const next = `${date}T${time}`;
    if (next !== value) onValueChange(next);
  };

  const onOpenChange = (next: boolean, details: BasePopover.Root.ChangeEventDetails) => {
    if (next) {
      setDraftDate(committed?.date ?? null);
    } else if (details.reason !== 'escape-key' && draftDate && committed) {
      commit(draftDate, committed.time!);
    }
    setOpen(next);
  };

  const chooseTime = (time: string) => {
    commit(draftDate ?? clampDateKey(today, min, max), time);
    setOpen(false);
  };

  const text = committed
    ? `${formatDateKeyShort(committed.date!)} ${formatTimeOfDay(committed.time!)}`
    : null;

  return (
    <BasePopover.Root open={open} onOpenChange={onOpenChange} modal="trap-focus">
      <PickerTrigger
        ref={triggerRef}
        text={text}
        placeholder={placeholder}
        icon={Clock}
        disabled={disabled}
        {...triggerProps}
      />
      <FormValue
        name={name}
        value={committed ? value : ''}
        required={required}
        onFocusRedirect={() => triggerRef.current?.focus()}
      />
      <BasePopover.Portal>
        <BasePopover.Positioner sideOffset={4} align="start" className="z-popover">
          <PopoverPopup
            aria-label="날짜와 시각 선택"
            initialFocus={firstGridFocus(gridRef)}
            className="p-row gap-row flex w-auto flex-col md:flex-row"
          >
            <Calendar
              selected={draftDate}
              today={today}
              gridRef={gridRef}
              initialFocus={pickerInitialFocus(draftDate ?? committed?.date ?? null, today, range)}
              onSelect={setDraftDate}
              controlRef={calendarRef}
              {...range}
              footer={
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!isSelectable(today, range)}
                    onClick={() => {
                      setDraftDate(today);
                      calendarRef.current?.show(today);
                    }}
                  >
                    오늘
                  </Button>
                  {!required && committed ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        onValueChange('');
                        setOpen(false);
                      }}
                    >
                      비우기
                    </Button>
                  ) : null}
                </>
              }
            />
            <TimeList
              selected={committed?.time ?? null}
              options={timeOfDayOptions(minuteStep, committed?.time ?? undefined)}
              onSelect={chooseTime}
            />
          </PopoverPopup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}

function TimeList({
  selected,
  options,
  onSelect,
}: {
  selected: string | null;
  options: string[];
  onSelect: (time: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [focusTime, setFocusTime] = useState(selected ?? options.find((t) => t >= '09:00')!);
  const initialTime = useRef(focusTime);
  const moveFocus = useRef(false);

  /* 열릴 때 고른 시각(없으면 오전 9시)이 목록 가운데 오게 한다 — 스크롤 애니메이션 없이. */
  useLayoutEffect(() => {
    const list = listRef.current;
    const target = list?.querySelector<HTMLElement>(`[data-time="${initialTime.current}"]`);
    if (list && target) list.scrollTop = target.offsetTop - list.clientHeight / 2;
  }, []);

  useLayoutEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    listRef.current?.querySelector<HTMLElement>(`[data-time="${focusTime}"]`)?.focus();
  }, [focusTime]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = options.indexOf(focusTime);
    const nextIndex = {
      ArrowUp: index - 1,
      ArrowDown: index + 1,
      Home: 0,
      End: options.length - 1,
    }[event.key];
    if (nextIndex === undefined) return;
    event.preventDefault();
    const next = options[Math.max(0, Math.min(options.length - 1, nextIndex))]!;
    moveFocus.current = true;
    setFocusTime(next);
  };

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-label="시각"
      className="border-line md:ps-row relative max-h-40 overflow-y-auto border-t pt-1 md:max-h-72 md:w-28 md:border-t-0 md:border-l md:pt-0"
    >
      {options.map((time) => {
        const isSelected = time === selected;
        return (
          <button
            key={time}
            type="button"
            role="option"
            data-time={time}
            aria-selected={isSelected}
            tabIndex={time === focusTime ? 0 : -1}
            onKeyDown={onKeyDown}
            onFocus={() => setFocusTime(time)}
            onClick={() => onSelect(time)}
            className={cn(
              'px-snug flex h-8 w-full items-center rounded-lg text-sm whitespace-nowrap tabular-nums',
              'ease-flow duration-fast transition-colors',
              isSelected
                ? 'bg-action text-action-fg hover:bg-action-hover font-semibold'
                : 'text-ink hover:bg-highlight',
            )}
          >
            {formatTimeOfDay(time)}
          </button>
        );
      })}
    </div>
  );
}
